import {
  createOrgTag,
  deleteOrgTags,
  getMyOrganization,
  getOrganizationBySlug,
  getOrganizationTags,
} from "@/api/organization/thunks";
import { Organization } from "@/types/organization";
import { OrganizationState, RootState } from "@/types/store";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export const FALLBACK_ORGANIZATION: Organization = {
  id: "",
  name: "MainSail",
  status: "Active",
  description: "",
  logo: "",
  slug: "",
  website: "",
  contactEmail: "",
  contactPhone: "",
  productionStreamlineToken: null,
  sandboxStreamlineToken: null,
  docusignTemplate: [],
  createdAt: "",
  updatedAt: "",
};

const initialState: OrganizationState = {
  organization: FALLBACK_ORGANIZATION,
  loading: false,
  error: null,
  tags: [],
  tagsLoading: false,
  tagsError: null,
};

// Keep the list ordered by name (case-insensitive) to match the BE's
// `orderBy: { name: "asc" }` after local create/append.
const sortByName = (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

const organizationSlice = createSlice({
  name: "OrganizationSlice",
  initialState,
  reducers: {
    setOrganization: (state, action: PayloadAction<Organization>) => {
      state.organization = action.payload;
    },
    clearOrganizationData: (state) => {
      state.organization = FALLBACK_ORGANIZATION;
      state.loading = false;
      state.error = null;
      state.tags = [];
      state.tagsLoading = false;
      state.tagsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMyOrganization.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMyOrganization.fulfilled, (state, action) => {
        state.organization = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(getMyOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to get organization";
      })
      .addCase(getOrganizationBySlug.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOrganizationBySlug.fulfilled, (state, action) => {
        state.organization = {
          ...FALLBACK_ORGANIZATION,
          id: action.payload.id,
          name: action.payload.name,
          slug: action.payload.slug,
          logo: action.payload.logo,
          contactEmail: action.payload.contactEmail,
        };
        state.loading = false;
        state.error = null;
      })
      .addCase(getOrganizationBySlug.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to get organization by slug";
      })

      // ── Property tags ──────────────────────────────────────────────────
      .addCase(getOrganizationTags.pending, (state) => {
        state.tagsLoading = true;
        state.tagsError = null;
      })
      .addCase(getOrganizationTags.fulfilled, (state, action) => {
        state.tagsLoading = false;
        state.tags = action.payload;
      })
      .addCase(getOrganizationTags.rejected, (state, action) => {
        state.tagsLoading = false;
        state.tagsError = (action.payload as string) || "Failed to load tags";
      })
      .addCase(createOrgTag.fulfilled, (state, action) => {
        // Append the new tag and re-sort to keep parity with the BE ordering.
        state.tags = [...state.tags, action.payload].sort(sortByName);
      })
      .addCase(deleteOrgTags.fulfilled, (state, action) => {
        const removed = new Set(action.payload.ids);
        state.tags = state.tags.filter((tag) => !removed.has(tag.id));
      });
  },
});

export const { setOrganization, clearOrganizationData } =
  organizationSlice.actions;

export const selectOrgTags = (state: RootState) => state.Organization.tags;
export const selectOrgTagsLoading = (state: RootState) =>
  state.Organization.tagsLoading;
export const selectOrgTagsError = (state: RootState) =>
  state.Organization.tagsError;

export default organizationSlice.reducer;
